import React, { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";

/** Rustige zee met trage golfbeweging (low-poly, lichte shader-vrije aanpak). */
function Sea() {
  const mesh = useRef();
  const geom = useMemo(() => ({ args: [120, 90, 56, 40] }), []);
  useFrame(({ clock }) => {
    const g = mesh.current?.geometry;
    if (!g) return;
    const t = clock.getElapsedTime() * 0.35;
    const pos = g.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i);
      pos.setZ(i, Math.sin(x * 0.25 + t) * 0.38 + Math.cos(y * 0.18 + t * 0.8) * 0.25);
    }
    pos.needsUpdate = true;
    g.computeVertexNormals();
  });
  return (
    <mesh ref={mesh} rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.6, -34]}>
      <planeGeometry {...geom} />
      <meshStandardMaterial color="#19a7c4" roughness={0.18} metalness={0.1} />
    </mesh>
  );
}

function Beach() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.2, 18]} receiveShadow>
      <planeGeometry args={[120, 40]} />
      <meshStandardMaterial color="#f2e4cb" roughness={0.9} />
    </mesh>
  );
}

/** Een paar tenten en caravans als eenvoudige volumes. */
function Camp() {
  const items = useMemo(() => ([
    { x: -19, z: 8, s: 0.95, tent: true },
    { x: -10, z: 16, s: 0.85, tent: false },
    { x: 3, z: 10, s: 0.9, tent: true },
    { x: 13, z: 17, s: 0.85, tent: false },
    { x: 22, z: 9, s: 0.8, tent: true },
  ]), []);
  const group = useRef();
  useFrame(({ clock }) => {
    if (group.current) group.current.rotation.y = Math.sin(clock.getElapsedTime() * 0.06) * 0.05;
  });
  return (
    <group ref={group}>
      {items.map((it) => (
        <group key={`${it.x}-${it.z}`} position={[it.x, -0.6, it.z]} scale={it.s}>
          {it.tent ? (
            <mesh rotation={[0, Math.PI / 4, 0]}>
              <coneGeometry args={[0.9, 1.2, 4]} />
              <meshStandardMaterial color="#ff7a4d" roughness={0.5} />
            </mesh>
          ) : (
            <mesh>
              <boxGeometry args={[1.8, 0.9, 0.9]} />
              <meshStandardMaterial color="#f2efe6" roughness={0.5} />
            </mesh>
          )}
        </group>
      ))}
      {[-22, -11, 7, 24].map((x) => (
        <group key={x} position={[x, -1.2, x > 0 ? 5 : 19]} scale={0.8}>
          <mesh position={[0, 1.2, 0]}>
            <cylinderGeometry args={[0.07, 0.1, 2.4, 6]} />
            <meshStandardMaterial color="#5a4632" />
          </mesh>
          <mesh position={[0, 2.5, 0]}>
            <sphereGeometry args={[0.75, 10, 8]} />
            <meshStandardMaterial color="#2e9e7a" roughness={0.7} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

/** Lichte 3D-scène achter het aanmeldscherm; valt stil terug op een gradient. */
export default function LoginScene() {
  return (
    <div className="absolute inset-0" aria-hidden="true" data-testid="login-scene">
      <Suspense fallback={null}>
        <Canvas dpr={[1, 1.5]} camera={{ position: [0, 13, 58], fov: 34 }} gl={{ antialias: true, powerPreference: "low-power" }}>
          <color attach="background" args={["#bfe8f2"]} />
          <fog attach="fog" args={["#d8f1f7", 70, 165]} />
          <hemisphereLight intensity={0.75} groundColor="#19a7c4" />
          <directionalLight position={[6, 10, 8]} intensity={1.35} color="#fff6e6" />
          <Sea />
          <Beach />
          <Camp />
        </Canvas>
      </Suspense>
    </div>
  );
}
